import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { useT } from '../lib/i18n';

export const AuthCallback: React.FC = () => {
  const { t } = useT();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Supabase OAuth callback handling
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        if (accessToken) {
          // Supabase handles session automatically
          setStatus('success');
          setTimeout(() => navigate('/brain-dump'), 1500);
          return;
        }

        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');

        if (!code || !state) {
          throw new Error(t('authCallback.authCodeNotFound'));
        }

        const result = await authService.handleCallback(code, state);
        localStorage.setItem('auth_token', result.token);
        localStorage.setItem('user', JSON.stringify(result.user));
        setStatus('success');
        setTimeout(() => navigate('/setup'), 2000);
      } catch (err) {
        setStatus('error');
        setError(err instanceof Error ? err.message : 'Unknown error');
      }
    };

    handleCallback();
  }, [navigate, t]);

  return (
    <div className="min-h-screen bg-bg-primary flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {status === 'loading' && (
          <div className="space-y-4">
            <div className="w-12 h-12 border-2 border-text-muted border-t-brand rounded-full animate-spin mx-auto" />
            <h2 className="text-xl font-bold text-text-primary">{t('authCallback.authenticating')}</h2>
            <p className="text-sm text-text-muted">{t('authCallback.pleaseWait')}</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-4">
            <div className="w-12 h-12 rounded-full bg-success/10 border border-success/30 flex items-center justify-center mx-auto">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-success">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-text-primary">{t('authCallback.authSuccess')}</h2>
            <p className="text-sm text-text-muted">{t('authCallback.navigatingHome')}</p>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-4">
            <div className="w-12 h-12 rounded-full bg-danger/10 border border-danger/30 flex items-center justify-center mx-auto">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="text-danger">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-text-primary">{t('authCallback.authFailed')}</h2>
            <p className="text-sm text-danger">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-2 px-5 py-2.5 rounded-xl text-[13px] font-semibold bg-brand text-white border-none cursor-pointer hover:bg-brand-hover transition-colors"
            >
              {t('loginPage.backToLogin')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
